/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import TextBlock from '@component/common/block/text-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import LoadingDots from '@design-system/loading-dots';
import * as color from '@constant/color';
import * as sizeConstants from '@constant/size';
import { ETheme, TIcon, TTone } from '@datatype/type';

interface IProps {
  theme?: ETheme;
  tone?: TTone;
  kind?: 'filled' | 'outlined';
  size?: 'small' | 'medium' | 'large';
  shape?: 'square' | 'round' | 'pill';
  width?: string;
  height?: string;
  text: string;
  icon?: TIcon;
  arrangement?: 'icon-text' | 'text-icon';
  disabled?: boolean;
  isLoading?: boolean;
  handleClick?: () => void;
}

const createDesign = (
  theme: ETheme,
  tone: TTone,
  btnSize: 'small' | 'medium' | 'large',
  kind: 'filled' | 'outlined',
  shape: 'square' | 'round' | 'pill',
  disabled: boolean,
  width?: string,
  height?: string,
) => {
  const createTheme = () => {
    if (disabled) {
      return {
        backgroundColor: color.GRAY_2,
        textColor: color.WHITE_1,
        borderColor: color.GRAY_2,
      };
    }

    switch (theme) {
      case ETheme.BROWN:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.BROWN_4 : (tone === 'strong' ? color.BROWN_1 : color.BROWN_2),
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.BROWN_1 : (tone === 'strong' ? color.BROWN_1 : color.BROWN_2),
          borderColor: tone === 'light' ? color.BROWN_1 : (tone === 'strong' ? color.BROWN_1 : color.BROWN_2),
        };
      case ETheme.GRAY:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.GRAY_4 : (tone === 'strong' ? color.BLACK_4 : color.GRAY_1),
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.BLACK_4 : (tone === 'strong' ? color.BLACK_4 : color.GRAY_1),
          borderColor: tone === 'light' ? color.BLACK_4 : (tone === 'strong' ? color.BLACK_4 : color.GRAY_1),
        };
      case ETheme.YELLOW:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.YELLOW_5 : color.YELLOW_1,
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.BLACK_3 : color.YELLOW_1,
          borderColor: tone === 'light' ? color.BLACK_3 : color.YELLOW_1,
        };
      case ETheme.GREEN:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.GREEN_5 : color.GREEN_3,
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.GREEN_1 : color.GREEN_3,
          borderColor: tone === 'light' ? color.GREEN_1 : color.GREEN_3,
        };
      case ETheme.RED:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.RED_5 : color.RED_3,
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.RED_1 : color.RED_3,
          borderColor: tone === 'light' ? color.RED_1 : color.RED_3,
        };
      case ETheme.BLUE:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.BLUE_5 : color.BLUE_2,
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.BLUE_1 : color.BLUE_2,
          borderColor: tone === 'light' ? color.BLUE_1 : color.BLUE_2,
        };
      case ETheme.CYAN:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.CYAN_5 : (tone === 'strong' ? color.CYAN_1 : color.CYAN_2),
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.CYAN_2 : (tone === 'strong' ? color.CYAN_1 : color.CYAN_2),
          borderColor: tone === 'light' ? color.CYAN_2 : (tone === 'strong' ? color.CYAN_1 : color.CYAN_2),
        };
      default:
        return {
          backgroundColor: kind === 'outlined' ? color.WHITE_1 : tone === 'light' ? color.GRAY_4 : color.BLACK_2,
          textColor: kind === 'filled' && tone !== 'light' ? color.WHITE_1 : tone === 'light' ? color.BLACK_2 : color.BLACK_2,
          borderColor: tone === 'light' ? color.BLACK_2 : color.BLACK_2,
        };
    }
  };

  const createSize = () => {
    const defaultHeight = {
      small: sizeConstants.DESIGN_SYSTEM_BUTTON_SMALL_HEIGHT,
      medium: sizeConstants.DESIGN_SYSTEM_BUTTON_MEDIUM_HEIGHT,
      large: sizeConstants.DESIGN_SYSTEM_BUTTON_LARGE_HEIGHT,
    }[btnSize];
    const borderRadius = {
      square: '0px',
      round: sizeConstants.BORDER_RADIUS_1,
      pill: sizeConstants.BORDER_RADIUS_2,
    }[shape];

    return {
      height: height && height !== 'auto' ? height : defaultHeight,
      borderRadius,
      textSize: btnSize,
      iconSize: btnSize,
    };
  };

  const createEvent = () => ({ cursor: disabled ? 'auto' : 'pointer' });

  const themeDesign = createTheme();
  const sizeDesign = createSize();

  const boxDesign = {
    borderRadius: sizeDesign.borderRadius,
    backgroundColor: themeDesign.backgroundColor,
    height: sizeDesign.height,
    width: '100%',
    event: createEvent(),
    gap: '5px',
    border: kind === 'outlined',
    borderWidth: kind === 'outlined' ? sizeConstants.DESIGN_SYSTEM_BUTTON_BORDER_WIDTH : '0px',
    borderColor: themeDesign.borderColor,
  };

  const textDesign = {
    size: sizeDesign.textSize,
    color: themeDesign.textColor,
  };

  const iconDesign = {
    size: sizeDesign.iconSize,
    kind: 'outlined',
    color: themeDesign.textColor,
  };

  return {
    width,
    height,
    box: boxDesign,
    text: textDesign,
    icon: iconDesign,
    loading: {
      backgroundColor: color.GRAY_DISABLE_BUTTON_COLOR,
      dotColor: color.WHITE_1,
      borderWidth: boxDesign.borderWidth,
    },
  };
};

const Wrapper = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  width: ${(props) => props.$design.width || 'fit-content'};
  height: ${(props) => props.$design.height || 'auto'};
`;

const LoadingContainer = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: ${(props) => props.$design.box.height};
  background-color: ${(props) => props.$design.loading.backgroundColor};
  border-radius: ${(props) => props.$design.box.borderRadius};
  border: ${(props) => props.$design.box.border
    ? `${props.$design.loading.borderWidth} solid ${props.$design.box.borderColor}`
    : 'none'};
`;

const TextButton: React.FC<IProps> = ({
  theme = ETheme.BROWN,
  tone = 'strong',
  kind = 'filled',
  size = 'medium',
  shape = 'square',
  width,
  height,
  text,
  icon,
  arrangement = 'text-icon',
  disabled = false,
  isLoading = false,
  handleClick,
}) => {
  const design = createDesign(theme, tone, size, kind, shape, disabled, width, height);

  return (
    <Wrapper $design={design}>
      {isLoading ? (
        <LoadingContainer $design={design}>
          <LoadingDots color={design.loading.dotColor} />
        </LoadingContainer>
      ) : icon ? (
        <IconTextBlock
          icon={icon}
          text={text}
          arrangement={arrangement}
          design={design as any}
          click={!disabled ? { handler: handleClick } : undefined}
        />
      ) : (
        <TextBlock text={text} design={design as any} click={!disabled ? { handler: handleClick } : undefined} />
      )}
    </Wrapper>
  );
};

export default TextButton;
